import { SvgIconComponent } from "@mui/icons-material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SupportIcon from "@mui/icons-material/Support";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CurrencyBitcoinIcon from "@mui/icons-material/CurrencyBitcoin";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import HubIcon from "@mui/icons-material/Hub";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PaymentsIcon from "@mui/icons-material/Payments";
import PoolIcon from "@mui/icons-material/Pool";
import HearingIcon from "@mui/icons-material/Hearing";
import EmailIcon from "@mui/icons-material/Email";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";

export type Era = "zip" | "paloEngineer" | "fourMation" | "paloSenior";

export type Project = {
  id: string;
  title: string;
  client: string;
  era: Era;
  color: string;
  bullets: string[];
  stack: string[];
  icon: SvgIconComponent;
};

export const NAME = "ELBERT WAN";
export const ROLE = "Typescript Fullstack Software Engineer";
export const TAGLINE = "8 years — Senior Consultant & Engineer";

export const STATS = [
  { value: 8, suffix: "", label: "years" },
  { value: 5, suffix: "", label: "industries" },
  { value: 15, suffix: "+", label: "products shipped" },
];

export const ERAS: {
  id: Era;
  label: string;
  years: string;
  color: string;
}[] = [
  { id: "zip", label: "Zip Co", years: "2018–2020", color: "#6E2FEB" },
  {
    id: "paloEngineer",
    label: "Palo IT — Engineer",
    years: "2020–2023",
    color: "#EF3340",
  },
  {
    id: "fourMation",
    label: "4Mation",
    years: "2023–2024",
    color: "#C084FC",
  },
  {
    id: "paloSenior",
    label: "Palo IT — Senior Consultant",
    years: "2024–2026",
    color: "#8B5CF6",
  },
];

export const PROJECTS: Project[] = [
  {
    id: "zip-contentful",
    title: "Contentful CMS Engine",
    client: "Zip Co",
    era: "zip",
    color: "#6E2FEB",
    bullets: [
      "Components for dynamic marketing pages — Black Friday, Christmas, Zip Pay",
      "Scripts to speed up designer uploads, saving hours per week",
    ],
    stack: ["React", "Contentful", "AWS Lambda"],
    icon: ShoppingCartIcon,
  },
  {
    id: "zip-dotnet",
    title: ".NET Microservices",
    client: "Zip Co",
    era: "zip",
    color: "#6E2FEB",
    bullets: [
      "Picked up .NET Core to maintain microservices and BAU",
      "MySQL data layer, Dynatrace observability",
    ],
    stack: [".NET Core / C#", "MySQL", "Dynatrace"],
    icon: ShoppingCartIcon,
  },
  {
    id: "bookingsg",
    title: "BookingSG V1 & RBS V2",
    client: "GovTech Singapore",
    era: "paloEngineer",
    color: "#EF3340",
    bullets: [
      "Complete booking system for admins, agency-admins and users",
      "V2: resources, announcements, notifications, locations, user groups",
    ],
    stack: ["React/Typescript", "Nodejs/Typescript"],
    icon: CalendarMonthIcon,
  },
  {
    id: "firstrescue",
    title: "First Rescue",
    client: "IAG",
    era: "paloEngineer",
    color: "#FF7A00",
    bullets: [
      "Whitelabel web-app linking customers to towing services, end to end",
      "Google Maps + ServiceNow for live roadside-assistance tracking",
      "Animations throughout the flow",
    ],
    stack: ["Nextjs", "Material UI", "Google Maps", "ServiceNow"],
    icon: SupportIcon,
  },
  {
    id: "hsbc",
    title: "Retail DCO/ICO",
    client: "HSBC",
    era: "paloEngineer",
    color: "#DB0011",
    bullets: [
      "New retail frontend uplifting the customer application journey",
    ],
    stack: ["React", "Nodejs", "Jenkins", "SonarQube"],
    icon: AccountBalanceIcon,
  },
  {
    id: "blocktrust",
    title: "Blocktrust",
    client: "Blockchain / NFT",
    era: "paloEngineer",
    color: "#E8B923",
    bullets: [
      "Refactored a white-label blockchain/NFT platform for multiple tenants",
    ],
    stack: ["Nextjs", "C# Backend"],
    icon: CurrencyBitcoinIcon,
  },
  {
    id: "whattruck",
    title: "WhatTruck",
    client: "Botany Access",
    era: "paloEngineer",
    color: "#2E9E5B",
    bullets: [
      "End-to-end marketplace app + web app for heavy-load trucking",
    ],
    stack: ["React Native/Typescript", "Nodejs/Typescript"],
    icon: LocalShippingIcon,
  },
  {
    id: "qvego",
    title: "QV / EGO AI Chatbot",
    client: "4Mation — Tech Lead",
    era: "fourMation",
    color: "#C084FC",
    bullets: [
      "Multiple Amazon Bedrock Claude agents + lambdas: recommendations, complaints, moderation",
      "Multi-agent collaboration for accuracy + latency; vector knowledge bases",
      "Deep-chat frontend + Retool dashboard to review conversations",
    ],
    stack: ["AWS Bedrock", "AWS Lambda", "React/Typescript"],
    icon: SmartToyIcon,
  },
  {
    id: "montgomery",
    title: "AI Email Categorizer",
    client: "Montgomery Homes",
    era: "fourMation",
    color: "#FDA4AF",
    bullets: [
      "OpenAI categorization on a cronjob with a rule engine to forward, label, move",
      "Lovable frontend + Strapi backend (SSO) to review job logs",
    ],
    stack: ["OpenAI", "Strapi", "Typescript Cronjob"],
    icon: EmailIcon,
  },
  {
    id: "ida",
    title: "Industrial Deafness Australia",
    client: "4Mation",
    era: "fourMation",
    color: "#7DD3FC",
    bullets: [
      "Rostering calendar (custom react-big-calendar) + react-flow workflow system",
      "Custom form builder and task management solution",
    ],
    stack: ["React/Typescript", "Node/Strapi"],
    icon: HearingIcon,
  },
  {
    id: "vista",
    title: "Vista — Agentic Orchestrator",
    client: "Palo IT — Senior Consultant",
    era: "paloSenior",
    color: "#8B5CF6",
    bullets: [
      "Extracted complex business logic into agentic workflows, rules and skills",
      "Agentic orchestrator running complex workflows with different models",
      "GitHub, Azure, Cursor, Copilot & OpenRouter integrations — tickets completed autonomously",
      "Implementation plans actioned through the orchestrator: 1980–2026 code, WinForms → Angular/C#",
    ],
    stack: ["Agentic Workflows", "OpenRouter", "Angular/C#"],
    icon: HubIcon,
  },
  {
    id: "imed",
    title: "IMOGEN Chatbot",
    client: "I-MED — Senior Consultant",
    era: "paloSenior",
    color: "#14B8A6",
    bullets: [
      "Middleware / chatbot handling AI messaging, escalating to Genesys human agents",
      "Solutioned and architected with the I-MED team — pre-delivery to go-live",
    ],
    stack: ["AI Chatbot", "Genesys", "Node/Typescript"],
    icon: SmartToyIcon,
  },
  {
    id: "applus",
    title: "Australian Payments Plus",
    client: "Palo IT — Senior Consultant",
    era: "paloSenior",
    color: "#FFD100",
    bullets: [
      "Frontend (detail unavailable due to NDA)",
      "Seminars & consulting: AI (Gen-e2) to code, plan and design with quality",
    ],
    stack: ["React/Typescript frontend", "Nodejs/Typescript backend", "Gen-e2"],
    icon: PaymentsIcon,
  },
  {
    id: "urbnsurf",
    title: "UrbnSurf — Phase 1 + Phase 1.5",
    client: "Palo IT — Senior Consultant",
    era: "paloSenior",
    color: "#0EA5E9",
    bullets: [
      "Full booking flow for guest and logged-in users with multiple flows",
      "Authentication system",
      "Dashboard",
      "Memberships, multipacks and gift cards",
      "Various other features including quick rebook",
      "Complete CMS system for custom pages and website data",
    ],
    stack: [
      "React/NextJs/Typescript frontend",
      "Nodejs/Typescript backend",
      "Figma Make",
    ],
    icon: PoolIcon,
  },
  {
    id: "virginactive",
    title: "Virgin Active",
    client: "Palo IT — Senior Consultant",
    era: "paloSenior",
    color: "#E10A0A",
    bullets: [
      "Consulted and developed new website and features across SG, TH & AU",
      "MyLocker frontend — new product and app for gym goers",
    ],
    stack: ["React", "Typescript"],
    icon: FitnessCenterIcon,
  },
];

export const projectById = (id: string): Project => {
  const project = PROJECTS.find((p) => p.id === id);
  if (!project) {
    throw new Error(`Unknown project: ${id}`);
  }
  return project;
};

// TODO(elbe): confirm the public contact email + LinkedIn URL for the CTAs
export const CONTACT = {
  email: "pibatsolutions@gmail.com",
  linkedIn: "https://www.linkedin.com/in/elbertwan",
};

export const STACK_CLUSTERS: { label: string; chips: string[] }[] = [
  {
    label: "TypeScript",
    chips: ["Typescript", "Node/Typescript", ".NET Core / C#", "Angular/C#"],
  },
  {
    label: "React",
    chips: ["React", "Nextjs", "React Native", "Material UI", "styled-components"],
  },
  {
    label: "Node",
    chips: ["Nodejs", "Strapi", "MySQL", "ServiceNow", "Contentful"],
  },
  {
    label: "AI",
    chips: [
      "AWS Bedrock",
      "OpenAI",
      "OpenRouter",
      "Agentic Workflows",
      "Gen-e2",
      "Genesys",
    ],
  },
  {
    label: "Cloud",
    chips: ["AWS Lambda", "Azure", "Jenkins", "SonarQube", "Dynatrace"],
  },
];
